import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useState } from 'react';
import { Box, Divider, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import { palette } from '../../theme';
import { UserAvatar } from '../user-avatar/user-avatar.component';

export interface Athlete {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface Role {
  id: string | number;
  name: string;
}

export interface EditAthleteDialogProps {
  isOpen: boolean;
  handleClose: any;
  handleSubmit: any;
  isLoading: boolean;
  athlete: Athlete;
  handleRemove?: () => void;
  hideMemberControls?: boolean;
}

export default function EditAthleteDialog(props: EditAthleteDialogProps) {
  const [roleOptions] = useState<Role[]>([
    { id: 1, name: 'Coach' },
    { id: 2, name: 'Athlete' },
  ]);
  const [role, setRole] = useState<Role>(
    roleOptions.find((option) => option.name.toLowerCase() === props.athlete.role?.toLowerCase()) ?? roleOptions[1]
  );

  const closeModal = () => {
    props.handleClose();
  };

  const submit = () => {
    props.handleSubmit({ id: props.athlete.id, roleId: role.id });
  };

  const handleRoleChange = (_: React.MouseEvent<HTMLElement>, newRoleName: string | null) => {
    if (!newRoleName) return;
    const selected = roleOptions.find((o) => o.name === newRoleName);
    if (selected) setRole(selected);
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={closeModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { width: 520, maxWidth: 520 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" component="div" fontWeight={700}>
          Edit Member
        </Typography>
        <IconButton
          aria-label="close"
          onClick={closeModal}
          size="small"
          sx={{
            color: palette.text.secondary,
            '&:hover': { bgcolor: palette.grey[100] },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        {/* Member info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <UserAvatar name={props.athlete.name || ''} size={44} fontSize="0.875rem" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" fontWeight={700} color={palette.text.primary} noWrap>
              {props.athlete.name}
            </Typography>
            <Typography variant="body2" color={palette.text.secondary} noWrap>
              {props.athlete.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2, borderColor: palette.divider }} />

        {/* Role selector */}
        {!props.hideMemberControls && (
          <>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: palette.text.primary }}>
              Role
            </Typography>
            <ToggleButtonGroup
              value={role.name}
              exclusive
              onChange={handleRoleChange}
              sx={{
                bgcolor: palette.grey[100],
                borderRadius: 2,
                p: 0.5,
                gap: 0.5,
                '& .MuiToggleButtonGroup-grouped': {
                  border: 'none',
                  borderRadius: '6px !important',
                },
              }}
            >
              <ToggleButton
                value="Athlete"
                sx={{
                  px: 2.5,
                  py: 1,
                  gap: 1,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  color: palette.text.secondary,
                  bgcolor: 'transparent',
                  '&.Mui-selected': {
                    bgcolor: palette.background.paper,
                    color: palette.text.primary,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
                    '&:hover': { bgcolor: palette.background.paper },
                  },
                  '&:hover': { bgcolor: palette.primary.light },
                }}
              >
                <DirectionsRunOutlinedIcon fontSize="small" />
                Athlete
              </ToggleButton>
              <ToggleButton
                value="Coach"
                sx={{
                  px: 2.5,
                  py: 1,
                  gap: 1,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  color: palette.text.secondary,
                  bgcolor: 'transparent',
                  '&.Mui-selected': {
                    bgcolor: palette.background.paper,
                    color: palette.text.primary,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
                    '&:hover': { bgcolor: palette.background.paper },
                  },
                  '&:hover': { bgcolor: palette.primary.light },
                }}
              >
                <AssignmentOutlinedIcon fontSize="small" />
                Coach
              </ToggleButton>
            </ToggleButtonGroup>
          </>
        )}
      </DialogContent>

      <Divider sx={{ borderColor: palette.divider }} />

      <DialogActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'space-between' }}>
        {props.handleRemove && !props.hideMemberControls ? (
          <Button onClick={props.handleRemove} variant="outlined" color="error" sx={{ mr: 'auto' }}>
            Remove from Team
          </Button>
        ) : (
          <span />
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={closeModal}
            variant="text"
            sx={{
              color: palette.text.secondary,
              '&:hover': { bgcolor: palette.grey[100] },
            }}
          >
            {props.hideMemberControls ? 'Close' : 'Cancel'}
          </Button>
          {!props.hideMemberControls && (
            <Button onClick={submit} disabled={props.isLoading} variant="contained" color="primary">
              Save Changes
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
